import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted } from "vue";
  import { useRoute } from "vue-router";
  import { useToast } from "vue-toastification";
  import builderApi from "@/services/api/builderApi";
  import facadeApi from "@/services/api/facadeApi";
  import {
    HouseDesignInterface,
    Floorplan,
  } from "@/types/HouseDesignInterface";
  import { FacadeInterface } from "@/types/FacadeInterface";
  import useVuelidate from "@vuelidate/core";
  import { required, minValue } from "@vuelidate/validators";

  interface TempFacade {
    tempId: string;
    name: string;
    description: string;
    imageFile: File | null;
    imagePath?: string;
    previewUrl?: string;
    isDefault: boolean;
    _id?: string;
  }

  interface HouseForm {
    _id?: string;
    name: string;
    description: string;
    area: number;
    width: number;
    length: number;
    bedroom: number;
    bathroom: number;
    garage: number;
    loungeRoom: number;
    storey: number;
    features: string[];
    basePrice: number;
    estateId?: string;
    builderId?: string;
    mainImages: string[];
    floorplans: Floorplan[];
    facades: FacadeInterface[];
  }

  const PLACEHOLDER_BUILDER_ID = "000000000000000000000000";

  
export default /*@__PURE__*/_defineComponent({
  ...{ name: "AdminEstateHomeDesigns" },
  __name: 'AdminEstateHomeDesigns',
  setup(__props, { expose: __expose }) {
  __expose();

  
  const route = useRoute();
  const toast = useToast();

  const { estateId } = route.params;
  const loading = ref(true);
  const saving = ref(false);
  const showModal = ref(false);
  const isEditMode = ref(false);
  const houseDesigns = ref<HouseDesignInterface[]>([]);
  const floorPlanInput = ref<HTMLInputElement | null>(null);
  const floorPlanFile = ref<File | null>(null);
  const floorPlanPreviewUrl = ref<string | null>(null);
  const facadeInputRefs = ref<Record<string, HTMLInputElement>>({});

  const tempFacades = ref<TempFacade[]>([]);

  const availableFeatures = [
    "Alfresco",
    "Covered Alfresco",
    "Home Theatre",
    "Study Nook",
    "Walk-in Pantry",
    "Butler's Pantry",
    "Ensuite",
    "Walk-in Robe",
    "Built-in Robes",
    "Separate Laundry",
    "Garden Courtyard",
    "Stone Benchtops",
    "Open Plan Living",
    "Linen Cupboard",
    "Double Garage",
    "Single Garage",
  ];

  const form = ref<HouseForm>({
    name: "",
    description: "",
    area: 0,
    width: 0,
    length: 0,
    bedroom: 0,
    bathroom: 0,
    garage: 0,
    loungeRoom: 1,
    storey: 1,
    features: [],
    basePrice: 0,
    mainImages: [],
    floorplans: [],
    facades: [],
  });

  const rules = computed(() => ({
    name: { required },
    area: { required, minValue: minValue(1) },
    bedroom: { required, minValue: minValue(0) },
    bathroom: { required, minValue: minValue(0) },
    garage: { required, minValue: minValue(0) },
    loungeRoom: { required, minValue: minValue(0) },
  }));

  const v$ = useVuelidate(rules, form);

  const configurationInput = computed(() => {
    if (
      form.value.bedroom ||
      form.value.bathroom ||
      form.value.garage ||
      form.value.loungeRoom
    ) {
      return `${form.value.bedroom} Bed / ${form.value.bathroom} Bath / ${form.value.garage} Car / ${form.value.loungeRoom} Living`;
    }

    return "";
  });

  const getConfiguration = (design: HouseDesignInterface) => {
    const bathrooms =
      typeof design.bathroom === "object"
        ? design.bathroom.quantity
        : design.bathroom;
    const living =
      typeof design.loungeRoom === "object"
        ? design.loungeRoom.quantity
        : design.loungeRoom;

    return `${design.bedroom} Bed / ${bathrooms} Bath / ${design.garage} Car / ${living} Living`;
  };

  const fetchDesigns = async () => {
    try {
      loading.value = true;
      const response = await builderApi.getHousesByEstateId(estateId as string);

      houseDesigns.value = response.data || [];
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { message?: string } };
      };

      console.error("Error fetching designs:", error);
      houseDesigns.value = [];

      if (err.response?.status !== 404) {
        toast.error(
          err.response?.data?.message || "Failed to fetch home designs"
        );
      }
    } finally {
      loading.value = false;
    }
  };

  const openCreateModal = () => {
    isEditMode.value = false;
    resetForm();
    showModal.value = true;
  };

  const openEditModal = (design: HouseDesignInterface) => {
    isEditMode.value = true;
    const bathrooms =
      typeof design.bathroom === "object"
        ? design.bathroom.quantity
        : design.bathroom;

    form.value = {
      _id: design._id,
      name: design.name,
      description: design.description || "",
      area: design.area,
      width: design.width,
      length: design.length,
      bedroom: design.bedroom,
      bathroom: bathrooms,
      garage: design.garage,
      loungeRoom: design.loungeRoom.quantity,
      storey: design.storey,
      features: design.features || [],
      basePrice: design.basePrice || 0,
      mainImages: design.mainImages || [],
      floorplans: design.floorplans || [],
      facades: design.facades || [],
    };

    // Load existing facades into temp state
    tempFacades.value = (design.facades || []).map((facade, index) => ({
      tempId: facade._id || `existing-${index}`,
      name: facade.name,
      description: facade.description || "",
      imageFile: null,
      imagePath: facade.url,
      previewUrl: facade.url,
      isDefault: index === 0, // First facade is default
      _id: facade._id,
    }));

    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    resetForm();
    v$.value.$reset();
  };

  const resetForm = () => {
    form.value = {
      name: "",
      description: "",
      area: 0,
      width: 0,
      length: 0,
      bedroom: 0,
      bathroom: 0,
      garage: 0,
      loungeRoom: 1,
      storey: 1,
      features: [],
      basePrice: 0,
      mainImages: [],
      floorplans: [],
      facades: [],
    };
    floorPlanFile.value = null;

    if (floorPlanPreviewUrl.value?.startsWith("blob:")) {
      URL.revokeObjectURL(floorPlanPreviewUrl.value);
    }

    floorPlanPreviewUrl.value = null;
    tempFacades.value.forEach((f) => {
      if (f.previewUrl?.startsWith("blob:")) URL.revokeObjectURL(f.previewUrl);
    });
    tempFacades.value = [];
  };

  const triggerFloorPlanUpload = () => {
    floorPlanInput.value?.click();
  };

  const handleFloorPlanUpload = (event: Event) => {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files[0]) {
      floorPlanFile.value = target.files[0];

      if (floorPlanPreviewUrl.value?.startsWith("blob:")) {
        URL.revokeObjectURL(floorPlanPreviewUrl.value);
      }

      if (target.files[0].type.startsWith("image/")) {
        floorPlanPreviewUrl.value = URL.createObjectURL(target.files[0]);
      } else {
        floorPlanPreviewUrl.value = null;
      }
    }
  };

  const addFacade = () => {
    if (tempFacades.value.length >= 5) return;

    tempFacades.value.push({
      tempId: `temp-${Date.now()}-${Math.random()}`,
      name: "",
      description: "",
      imageFile: null,
      isDefault: tempFacades.value.length === 0,
    });
  };

  const removeFacade = (tempId: string) => {
    const removedFacade = tempFacades.value.find((f) => f.tempId === tempId);

    if (removedFacade?.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(removedFacade.previewUrl);
    }

    tempFacades.value = tempFacades.value.filter((f) => f.tempId !== tempId);

    // If we removed the default facade, set the first one as default
    if (removedFacade?.isDefault && tempFacades.value.length > 0) {
      tempFacades.value[0].isDefault = true;
    }
  };

  const setDefaultFacade = (tempId: string) => {
    tempFacades.value.forEach((f) => {
      f.isDefault = f.tempId === tempId;
    });
  };

  const triggerFacadeUpload = (tempId: string) => {
    facadeInputRefs.value[tempId]?.click();
  };

  const handleFacadeUpload = (tempId: string, event: Event) => {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files[0]) {
      const facade = tempFacades.value.find((f) => f.tempId === tempId);

      if (facade) {
        if (facade.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(facade.previewUrl);
        }

        facade.imageFile = target.files[0];
        facade.previewUrl = URL.createObjectURL(target.files[0]);
      }
    }
  };

  const saveDesign = async () => {
    v$.value.$touch();
    const isValid = await v$.value.$validate();

    if (!isValid) {
      return;
    }

    try {
      saving.value = true;

      // 1. Create or update each facade as a separate entity, collect IDs
      // Sort so the default facade is first (it drives the preview image everywhere)
      const orderedFacades = [
        ...tempFacades.value.filter((f) => f.isDefault),
        ...tempFacades.value.filter((f) => !f.isDefault),
      ];
      const facadeIds: string[] = [];

      for (const facade of orderedFacades) {
        let url = facade.imagePath || "";

        // Upload new image if provided
        if (facade.imageFile) {
          const formData = new FormData();

          formData.append("files", facade.imageFile);
          // Use house image upload endpoint — it doesn't validate builderId existence
          const res = await builderApi.uploadHouseImages(
            formData,
            PLACEHOLDER_BUILDER_ID
          );

          url = res.data[0].value.Location;
        }

        if (facade._id) {
          // Update existing facade entity
          await facadeApi.updateFacade(
            { name: facade.name, description: facade.description, url },
            facade._id
          );
          facadeIds.push(facade._id);
        } else {
          // Create new facade entity via estate endpoint (no builder required)
          const created = await facadeApi.createFacadeForEstate({
            name: facade.name,
            description: facade.description,
            url,
            storeys: form.value.storey || 1,
          });

          facadeIds.push((created as any)._id);
        }
      }

      // 2. Upload floor plan file if a new one was selected
      let floorplans = form.value.floorplans;

      if (floorPlanFile.value) {
        const formData = new FormData();

        formData.append("files", floorPlanFile.value);
        const res = await builderApi.uploadHouseImages(
          formData,
          PLACEHOLDER_BUILDER_ID
        );
        const url = res.data[0].value.Location;

        floorplans = [{ url }];
      }

      // 3. Save house design with facade IDs
      const payload = {
        ...form.value,
        facades: facadeIds,
        floorplans,
        estateId: estateId as string,
        builderId: PLACEHOLDER_BUILDER_ID,
        bathroom: {
          quantity: form.value.bathroom,
          options: [],
        },
        loungeRoom: {
          quantity: form.value.loungeRoom,
          urls: [],
        },
        kitchens: [],
        inclusions: [],
        alfresco: false,
        inclusionsPack: [],
        optionalUpgrades: [],
        isActive: true,
      };

      if (isEditMode.value && form.value._id) {
        await builderApi.updateHouse(
          payload as unknown as HouseDesignInterface
        );
        toast.success("Home design updated successfully");
      } else {
        await builderApi.createHouse(
          payload as unknown as HouseDesignInterface
        );
        toast.success("Home design created successfully");
      }

      closeModal();
      await fetchDesigns();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };

      console.error("Error saving design:", error);
      toast.error(err.response?.data?.message || "Failed to save home design");
    } finally {
      saving.value = false;
    }
  };

  const confirmDelete = async (design: HouseDesignInterface) => {
    if (!confirm(`Are you sure you want to delete "${design.name}"?`)) {
      return;
    }

    try {
      await builderApi.deleteHouse(design._id);
      toast.success("Home design deleted successfully");
      await fetchDesigns();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };

      toast.error(
        err.response?.data?.message || "Failed to delete home design"
      );
    }
  };

  onMounted(() => {
    fetchDesigns();
  });

const __returned__ = { route, toast, estateId, loading, saving, showModal, isEditMode, houseDesigns, floorPlanInput, floorPlanFile, floorPlanPreviewUrl, facadeInputRefs, tempFacades, availableFeatures, form, rules, v$, configurationInput, getConfiguration, fetchDesigns, openCreateModal, openEditModal, closeModal, resetForm, triggerFloorPlanUpload, handleFloorPlanUpload, addFacade, removeFacade, setDefaultFacade, triggerFacadeUpload, handleFacadeUpload, PLACEHOLDER_BUILDER_ID, saveDesign, confirmDelete, ref, computed, onMounted, get useRoute() { return useRoute }, get useToast() { return useToast }, get builderApi() { return builderApi }, get facadeApi() { return facadeApi }, get HouseDesignInterface() { return HouseDesignInterface }, get Floorplan() { return Floorplan }, get FacadeInterface() { return FacadeInterface }, get useVuelidate() { return useVuelidate }, get required() { return required }, get minValue() { return minValue } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})